import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";

const FILTERS = [
  { id: "todos", label: "Todos" },
  { id: "destinos", label: "Destinos" },
  { id: "atendimento", label: "Atendimento" },
  { id: "dhl-cambio", label: "DHL & Câmbio" },
  { id: "loja", label: "Loja & Moda" },
];

const ITEMS = [
  {
    id: "escritorio-real",
    cat: "atendimento",
    caption: "Nosso escritório — atendimento real",
    img: "/images/escritorio.jpeg",
  },
  {
    id: "paris",
    cat: "destinos",
    caption: "Paris, França",
    img: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "rio",
    cat: "destinos",
    caption: "Rio de Janeiro, Brasil",
    img: "https://images.unsplash.com/photo-1483729558449-99ef09a8c325?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "atendimento-clientes",
    cat: "atendimento",
    caption: "Clientes prontos para embarcar",
    img: "https://images.unsplash.com/photo-1488646953014-85cb44e25828?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "logistica-dhl",
    cat: "dhl-cambio",
    caption: "Logística internacional DHL",
    img: "https://images.unsplash.com/photo-1494412574643-ff11b0a5c1c3?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "londres",
    cat: "destinos",
    caption: "Londres, Inglaterra",
    img: "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "cambio",
    cat: "dhl-cambio",
    caption: "Câmbio & remessas internacionais",
    img: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "assessoria",
    cat: "atendimento",
    caption: "Assessoria documental completa",
    img: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "praia",
    cat: "destinos",
    caption: "Litoral paradisíaco",
    img: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?q=80&w=1000&auto=format&fit=crop",
  },
  {
    id: "loja-araras",
    cat: "loja",
    caption: "Araras da loja — moda masculina",
    img: "/images/loja-araras.jpeg",
  },
  {
    id: "loja-moda",
    cat: "loja",
    caption: "Moda fitness e feminina",
    img: "/images/loja-fitness.jpeg",
  },
  {
    id: "loja-entrada",
    cat: "loja",
    caption: "Entrada da nossa loja em Cuiabá",
    img: "/images/loja-entrada.jpeg",
  },
  {
    id: "dubai",
    cat: "destinos",
    caption: "Dubai, Emirados Árabes",
    img: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=1000&auto=format&fit=crop",
  },
];

export const Gallery = () => {
  const [filter, setFilter] = useState("todos");
  const visible = ITEMS.filter((i) => filter === "todos" || i.cat === filter);

  return (
    <section id="galeria" data-testid="gallery-section" className="bg-ivory py-20 lg:py-32">
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-gold-bronze mb-5">
            Galeria
          </p>
          <h2
            data-testid="gallery-title"
            className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight leading-snug text-navy max-w-2xl"
          >
            Experiências e destinos que{" "}
            <em className="italic text-gold-bronze">já conectamos.</em>
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="gallery-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`gallery-filter-${f.id}`}
                className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 ${
                  filter === f.id
                    ? "bg-navy text-gold shadow-lg"
                    : "bg-sand text-navy/60 hover:text-navy hover:bg-gold/20"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Reveal>

        <motion.div layout className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((item) => (
              <motion.figure
                layout
                key={item.id}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.92 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                data-testid={`gallery-item-${item.id}`}
                className="group relative overflow-hidden rounded-2xl aspect-[4/3] bg-navy"
              >
                <img
                  src={item.img}
                  alt={item.caption}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                <figcaption className="absolute bottom-0 left-0 p-5 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                  <span className="font-serif italic text-lg text-ivory">{item.caption}</span>
                </figcaption>
              </motion.figure>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
